import React, { useState } from 'react';
import styles from './styles.module.css';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { setBranchId } from '../../../auth/actions';
import { Button, Input, Dropdown } from '../../../../common/ui';
import CustomerTable from './CustomerTable';
import Axios from '../../../../common/util/Axios';
import store from '../../../../store';

const Customers = ({
  customersState,
  auth: { branchList, branchId },
  setBranchId,
}) => {
  const [searchKeyword, setSearchKeyword] = useState('');

  const columns = [
    { id: 'name', label: 'Name' },
    { id: 'phone', label: 'Phone' },
    { id: 'email', label: 'Email' },
    { id: 'nic', label: 'NIC' },
    { id: 'address', label: 'Address' },
    { id: 'age', label: 'Age' },
    { id: 'gender', label: 'Gender' },
  ];

  const formatCsvValue = (value) => {
    if (value === null || value === undefined) return '""';
    const stringValue = String(value).replace(/"/g, '""');
    return `"${stringValue}"`;
  };

  const getTimestampString = () => {
    const pad = (num) => String(num).padStart(2, '0');
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}_${pad(d.getHours())}-${pad(d.getMinutes())}-${pad(d.getSeconds())}`;
  };

  const exportCustomersToCsv = (rows, cols, filename) => {
    const header = cols.map((c) => formatCsvValue(c.label)).join(',');
    const body = (rows || [])
      .map((row) => cols.map((c) => formatCsvValue(row[c.id])).join(','))
      .join('\n');
    const csvContent = `\ufeff${header}${body ? `\n${body}` : ''}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const branchOptions = (branchList || []).map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const handleChangeBranch = (value) => {
    setBranchId(value);
  };

  const handleExport = async () => {
    try {
      const requestBody = {
        filename: 'customers.xlsx',
        sheetName: 'Customers',
        columns: [
          { key: 'name', header: 'Name' },
          { key: 'phone', header: 'Phone' },
          { key: 'email', header: 'Email' },
          { key: 'nic', header: 'NIC' },
          { key: 'address', header: 'Address' },
          { key: 'age', header: 'Age' },
          { key: 'gender', header: 'Gender' },
        ],
        companyId: store.getState().authState.companyId,
      };

      const response = await Axios.post(
        '/api/dashboard/customers/export-customers-excel',
        requestBody,
        {
          responseType: 'blob',
          headers: {
            Accept:
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
        }
      );

      const contentDisposition = response?.headers?.['content-disposition'];
      let downloadFileName =
        requestBody.filename || `export_${getTimestampString()}.xlsx`;
      if (contentDisposition && contentDisposition.includes('filename=')) {
        const match = contentDisposition.match(
          /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i
        );
        downloadFileName = decodeURIComponent(
          match?.[1] || match?.[2] || downloadFileName
        );
      }

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', downloadFileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      try {
        const rows = customersState?.customersData || [];
        const filename = `customers_export_${getTimestampString()}.csv`;
        exportCustomersToCsv(rows, columns, filename);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Export failed', error);
      }
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.pageTitle}>Customers</h1>
          <p className={styles.breadcrumb}>CRM / Customers</p>
        </div>
        {branchId != null && branchOptions.length > 0 && (
          <div className={styles.branchWrap}>
            <Dropdown
              label="Branch"
              options={branchOptions}
              value={branchId}
              onChange={handleChangeBranch}
              placeholder="Select branch"
            />
          </div>
        )}
      </div>
      <div className={styles.body}>
        <div className={styles.card}>
          <div className={styles.toolbar}>
            <div className={styles.toolbarRight}>
              <div className={styles.searchWrap}>
                <Input
                  type="text"
                  placeholder="Search..."
                  value={searchKeyword}
                  onChange={(value) => setSearchKeyword(value)}
                />
              </div>
              <Button variant="secondary" onClick={handleExport}>
                Export
              </Button>
            </div>
          </div>
          <div className={styles.tableWrap}>
            <CustomerTable searchKeyword={searchKeyword} />
          </div>
        </div>
      </div>
    </div>
  );
};

Customers.propTypes = {
  customersState: PropTypes.object.isRequired,
  auth: PropTypes.shape({
    branchList: PropTypes.array,
    branchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  setBranchId: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  customersState: state.customersState,
  auth: state.authState,
});

export default connect(mapStateToProps, { setBranchId })(Customers);
